import {
  evaluateAllLogic,
  isQuestionVisible,
} from "@/components/features/FormBuilder/utils/evaluateLogic";
import { isBlankAnswer } from "@/components/features/FormBuilder/utils/isBlankAnswer";

import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import type { FormPage, FormQuestion } from "@/components/features/FormBuilder/types/form.types";

// Form Manager has no "Other" sentinel: the bridge files any choice value that is not a
// declared option id under otherText, and hands it back the same way on read.
export const OTHER_SENTINEL = "__other__";
const CHOICE_TYPES = new Set(["radio", "checkbox", "dropdown"]);

export const OTHER_TEXT_SUFFIX = "__other";

const otherKey = (questionId: string) => `${questionId}${OTHER_TEXT_SUFFIX}`;

function listQuestions(pages: FormPage[]): FormQuestion[] {
  return pages.flatMap((page) =>
    (page.sections ?? []).flatMap((section) => section.questions ?? []),
  );
}

function acceptsOther(question: FormQuestion): boolean {
  return CHOICE_TYPES.has(question.type) && question.allowOther === true;
}

function readOtherText(data: Record<string, unknown>, questionId: string): string | null {
  const text = data[otherKey(questionId)];
  return typeof text === "string" && text.trim() !== "" ? text.trim() : null;
}

function resolveOtherSentinel(
  question: FormQuestion,
  value: unknown,
  data: Record<string, unknown>,
): unknown {
  if (!acceptsOther(question)) return value;
  const otherText = readOtherText(data, question.id);
  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      entry !== OTHER_SENTINEL ? [entry] : otherText ? [otherText] : [],
    );
  }
  return value === OTHER_SENTINEL ? otherText : value;
}

// A repeating-group item keeps its children's Other text under `<childId>__other`, like the
// form does; Form Manager would reject that key as an unknown child field (SERV-3518).
function mapGroupItems(
  group: FormQuestion,
  value: unknown,
  mapItem: (fields: FormQuestion[], item: Record<string, unknown>) => Record<string, unknown>,
): unknown {
  if (!Array.isArray(value)) return value;
  const fields = group.fields ?? [];
  return value.map((item) =>
    typeof item === "object" && item !== null && !Array.isArray(item)
      ? mapItem(fields, item as Record<string, unknown>)
      : item,
  );
}

function foldGroupItem(fields: FormQuestion[], item: Record<string, unknown>) {
  const folded = { ...item };
  for (const field of fields) {
    if (!acceptsOther(field)) continue;
    if (field.id in folded) folded[field.id] = resolveOtherSentinel(field, item[field.id], item);
    delete folded[otherKey(field.id)];
  }
  return folded;
}

function unfoldGroupItem(fields: FormQuestion[], item: Record<string, unknown>) {
  const unfolded = { ...item };
  for (const field of fields) restoreOtherSentinel(field, item[field.id], unfolded);
  return unfolded;
}

// Splits the free text the bridge returned for an Other answer back into the sentinel and
// its companion `<questionId>__other` key, writing both into `values`.
function restoreOtherSentinel(
  question: FormQuestion,
  value: unknown,
  values: Record<string, unknown>,
): void {
  if (value === undefined || !acceptsOther(question)) return;
  const optionIds = new Set((question.options ?? []).map((option) => option.id));
  const isOther = (entry: unknown) => typeof entry === "string" && !optionIds.has(entry);

  if (Array.isArray(value)) {
    const otherEntries = value.filter(isOther);
    if (otherEntries.length === 0) return;
    values[question.id] = [...value.filter((entry) => !isOther(entry)), OTHER_SENTINEL];
    values[otherKey(question.id)] = otherEntries.join(", ");
  } else if (isOther(value)) {
    values[question.id] = OTHER_SENTINEL;
    values[otherKey(question.id)] = value;
  }
}

export function listVisibleQuestions(
  pages: FormPage[],
  data: Record<string, unknown>,
  context?: FormLogicContext,
): FormQuestion[] {
  const evalResult = evaluateAllLogic(pages, data, context);
  return pages
    .filter((page) => !evalResult.hiddenPages.has(page.id))
    .flatMap((page) => page.sections ?? [])
    .filter((section) => !evalResult.hiddenSections.has(section.id))
    .flatMap((section) => section.questions ?? [])
    .filter((question) => isQuestionVisible(question.id, question, evalResult));
}

// A skipped question still holds its input's empty value ("", null, [], false).
// Form Manager rejects several of those and fails the whole submission on one
// (SERV-3210), so a blank answer is left out rather than sent. So is an answer
// typed before branching hid its question: the filler no longer sees it.
export function toFormManagerAnswers(
  pages: FormPage[],
  data: Record<string, unknown>,
  context?: FormLogicContext,
): Record<string, unknown> {
  const answers: Record<string, unknown> = {};
  for (const question of listVisibleQuestions(pages, data, context)) {
    const value =
      question.type === "repeating-group"
        ? mapGroupItems(question, data[question.id], foldGroupItem)
        : resolveOtherSentinel(question, data[question.id], data);
    if (isBlankAnswer(value, question.type)) continue;
    answers[question.id] = value;
  }
  return answers;
}

export function fromFormManagerAnswers(
  pages: FormPage[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const values: Record<string, unknown> = { ...answers };
  for (const question of listQuestions(pages)) {
    const value = answers[question.id];
    if (question.type === "repeating-group") {
      if (value !== undefined) {
        values[question.id] = mapGroupItems(question, value, unfoldGroupItem);
      }
      continue;
    }
    restoreOtherSentinel(question, value, values);
  }
  return values;
}
